import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import tokenStorage from '../lib/tokenStorage';
import { login as keycloakLogin, refreshAccessToken, keycloakLogout } from '../api/authClient';
import logger from '../lib/logger';

const AuthContext = createContext(null);

export const ROLE_HOME_PATHS = {
  'FMPS-ADMIN': '/admin',
  'FMPS-ACCOUNT-MGR': '/manager',
  'FMPS-TELLER': '/teller/till',
  'FMPS-PAYMENT-CLERK': '/sales-rep',
  'FMPS-AGENT': '/main-agent',
  'FMPS-GOV': '/slpta',
  'FMPS-OPS-OFFICER': '/operations',
  'FMPS-OPS-SUPERVISOR': '/ops-supervisor',
  'FMPS-TRANSPORTER': '/mtc',
  'FMPS-DEMO': '/manager',
};

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry
const SESSION_WARNING_BUFFER_MS = 2 * 60 * 1000; // 2 minutes before expiry

const extractRole = (decodedToken) => {
  if (!decodedToken?.authorities) return null;
  return decodedToken.authorities.find((auth) => auth.startsWith('FMPS-')) || null;
};

const getTokenExpiry = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to ms
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const refreshTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const scheduleTokenRefresh = useCallback((accessToken, refreshTokenValue) => {
    clearTimers();

    const expiry = getTokenExpiry(accessToken);
    if (!expiry) return;

    const now = Date.now();
    const refreshIn = Math.max(expiry - now - TOKEN_REFRESH_BUFFER_MS, 0);
    const warnIn = Math.max(expiry - now - SESSION_WARNING_BUFFER_MS, 0);

    // Schedule warning
    warningTimerRef.current = setTimeout(() => {
      setShowSessionWarning(true);
    }, warnIn);

    // Schedule refresh
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const data = await refreshAccessToken(refreshTokenValue);
        const newAccessToken = data.access_token;
        const newRefreshToken = data.refresh_token || refreshTokenValue;

        tokenStorage.setAccessToken(newAccessToken);
        tokenStorage.setRefreshToken(newRefreshToken);
        setShowSessionWarning(false);

        // Schedule next refresh
        scheduleTokenRefresh(newAccessToken, newRefreshToken);
        logger.debug('Token refreshed successfully');
      } catch (error) {
        logger.error('Token refresh failed:', error);
        // Force logout on refresh failure
        logout();
      }
    }, refreshIn);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- logout is defined below; including it would create a circular dependency
  }, [clearTimers]);

  const logout = useCallback(async () => {
    clearTimers();
    setShowSessionWarning(false);

    const refreshTokenValue = tokenStorage.getRefreshToken();
    if (refreshTokenValue) {
      try {
        await keycloakLogout(refreshTokenValue);
      } catch (error) {
        logger.error('Keycloak logout error:', error);
      }
    }

    tokenStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/login';
  }, [clearTimers]);

  const login = useCallback(async (username, password) => {
    const data = await keycloakLogin(username, password);
    const { access_token, refresh_token } = data;

    const decodedToken = jwtDecode(access_token);
    const role = extractRole(decodedToken);

    if (!role) {
      throw new Error('No FMPS role found');
    }

    const userData = { name: username, role };

    tokenStorage.setAccessToken(access_token);
    tokenStorage.setRefreshToken(refresh_token);
    tokenStorage.setUserData(userData);

    setUser(userData);
    setIsAuthenticated(true);

    // Schedule token refresh
    scheduleTokenRefresh(access_token, refresh_token);

    return { role, homePath: ROLE_HOME_PATHS[role] || '/login' };
  }, [scheduleTokenRefresh]);

  const extendSession = useCallback(async () => {
    setShowSessionWarning(false);
    const refreshTokenValue = tokenStorage.getRefreshToken();
    if (refreshTokenValue) {
      try {
        const data = await refreshAccessToken(refreshTokenValue);
        const newAccessToken = data.access_token;
        const newRefreshToken = data.refresh_token || refreshTokenValue;

        tokenStorage.setAccessToken(newAccessToken);
        tokenStorage.setRefreshToken(newRefreshToken);
        scheduleTokenRefresh(newAccessToken, newRefreshToken);
        logger.debug('Session extended successfully');
      } catch (error) {
        logger.error('Session extension failed:', error);
        logout();
      }
    }
  }, [scheduleTokenRefresh, logout]);

  // Initialize auth state from storage on mount
  useEffect(() => {
    tokenStorage.hydrateFromLocalStorage();
    const token = tokenStorage.getAccessToken();
    const storedUser = tokenStorage.getUserData();

    if (token && storedUser) {
      const expiry = getTokenExpiry(token);
      if (expiry && expiry > Date.now()) {
        setUser(storedUser);
        setIsAuthenticated(true);

        const refreshTokenValue = tokenStorage.getRefreshToken();
        if (refreshTokenValue) {
          scheduleTokenRefresh(token, refreshTokenValue);
        }
      } else {
        // Token expired, clear everything
        tokenStorage.clear();
      }
    }
    setIsLoading(false);
  }, [scheduleTokenRefresh]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    extendSession,
    showSessionWarning,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
